import api from '../services/api';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

/**
 * Simple S3 Link Handler
 * Just handles clicks on existing S3 links, doesn't try to format HTML
 */

interface S3LinkData {
  filename: string;
  bucket: string;
  originalUrl: string;
}

class SimpleS3Handler {
  private isInitialized = false;
  private observer: MutationObserver | null = null;
  /** Avoid duplicate listeners (bind() was a new fn each time; chat streaming remounts nodes often). */
  private handledLinks = new WeakSet<Element>();
  private mutationRafId: number | null = null;
  private readonly boundS3Click = (e: Event) => {
    void this.handleS3LinkClick(e);
  };

  /**
   * Initialize the handler
   */
  public init(): void {
    if (this.isInitialized) return;

    // Handle existing links
    this.attachHandlers();

    // Set up mutation observer for dynamically added content
    this.setupMutationObserver();

    this.isInitialized = true;
  }

  /**
   * Clean up the handler
   */
  public destroy(): void {
    if (this.mutationRafId != null) {
      cancelAnimationFrame(this.mutationRafId);
      this.mutationRafId = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.removeAllHandlers();
    this.isInitialized = false;
  }

  /**
   * Parse S3 URL to extract bucket and filename
   */
  private parseS3Url(url: string): S3LinkData | null {
    try {
      const urlObj = new URL(url);

      // Check if it's a DigitalOcean Spaces URL
      if (!urlObj.hostname.includes('digitaloceanspaces.com')) {
        return null;
      }

      // Extract path segments
      const pathSegments = urlObj.pathname.split('/').filter(segment => segment.length > 0);

      if (pathSegments.length < 2) {
        return null;
      }

      const bucket = pathSegments[0];
      const filename = pathSegments.slice(1).join('/');

      return {
        filename,
        bucket,
        originalUrl: url
      };
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[SimpleS3Handler] Error parsing S3 URL:', error, url);
      }
      return null;
    }
  }

  /**
   * Get presigned URL from Flask API
   */
  private async getPresignedUrl(filename: string, bucket: string): Promise<string> {
    try {
      const requestData = {
        filename,
        bucket
      };

      // Use the main Flask API for S3 presigned URLs
      const response = await api.post('/s3/presigned-url', requestData);

      if (!response.data.presigned_url) {
        throw new Error('No presigned URL in response');
      }

      return response.data.presigned_url;
    } catch (error) {
      console.error('[SimpleS3Handler] Error generating presigned URL:', error);
      console.error('[SimpleS3Handler] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        status: (error as any)?.response?.status,
        data: (error as any)?.response?.data
      });
      throw error;
    }
  }

  /**
   * Handle S3 link click
   */
  private async handleS3LinkClick(event: Event): Promise<void> {
    const link = event.target as HTMLAnchorElement;

    if (!link || link.tagName !== 'A') {
      return;
    }

    // Prevent default navigation
    event.preventDefault();
    event.stopPropagation();

    // Get the URL from the href attribute, not the innerHTML
    const originalUrl = link.getAttribute('href');

    if (!originalUrl) {
      console.error('[SimpleS3Handler] No href attribute found on link');
      return;
    }

    // Parse the URL
    const s3Data = this.parseS3Url(originalUrl);
    if (!s3Data) {
      this.openUrl(originalUrl);
      return;
    }

    // Show loading state
    this.setLinkLoading(link, true);

    try {
      const presignedUrl = await this.getPresignedUrl(s3Data.filename, s3Data.bucket);

      // Open presigned URL using Capacitor Browser
      await this.openUrl(presignedUrl);
    } catch (error) {
      console.error('[SimpleS3Handler] Error handling S3 link:', error);

      // Show error state briefly
      this.setLinkError(link, true);

      // Fallback: open original URL after a short delay
      setTimeout(async () => {
        await this.openUrl(originalUrl);
        this.setLinkError(link, false);
      }, 2000);
    } finally {
      // Remove loading state
      this.setLinkLoading(link, false);
    }
  }

  /**
   * Set loading state on link
   */
  private setLinkLoading(link: HTMLAnchorElement, isLoading: boolean): void {
    if (isLoading) {
      link.classList.add('s3-link-loading');
      link.style.pointerEvents = 'none';
      link.style.opacity = '0.7';
    } else {
      link.classList.remove('s3-link-loading');
      link.style.pointerEvents = '';
      link.style.opacity = '';
    }
  }

  /**
   * Set error state on link
   */
  private setLinkError(link: HTMLAnchorElement, hasError: boolean): void {
    if (hasError) {
      link.classList.add('s3-link-error');
    } else {
      link.classList.remove('s3-link-error');
    }
  }

  /**
   * Open URL using Capacitor Browser or fallback to window.open
   */
  private async openUrl(url: string): Promise<void> {
    try {
      // Validate URL before opening
      if (!url || typeof url !== 'string') {
        console.error('[SimpleS3Handler] Invalid URL provided:', url);
        return;
      }

      // Check if URL contains HTML tags (which would cause the error we saw)
      if (url.includes('<') || url.includes('>') || url.includes('%3C') || url.includes('%3E')) {
        console.error('[SimpleS3Handler] URL contains HTML tags, this is invalid:', url);
        return;
      }

      // Check if we're running in a Capacitor app
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url });
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('[SimpleS3Handler] Error opening URL:', error);
      // Final fallback to window.open
      window.open(url, '_blank');
    }
  }

  private attachHandlerToLink(link: Element): void {
    if (this.handledLinks.has(link)) {
      return;
    }

    const href = link.getAttribute('href');

    // Check for the specific error pattern we're seeing
    if (href && (href.includes('%3Ca%20href=') || href.includes('<a href='))) {
      console.error('[SimpleS3Handler] Malformed href on S3 link:', href);
      this.handledLinks.add(link);
      return;
    }

    // Validate the href attribute
    if (!href || href.includes('<') || href.includes('>')) {
      console.error('[SimpleS3Handler] Invalid href on S3 link:', href);
      this.handledLinks.add(link);
      return;
    }

    this.handledLinks.add(link);
    link.addEventListener('click', this.boundS3Click);
  }

  /**
   * Attach click handlers to S3 links
   */
  private attachHandlers(): void {
    const s3Links = document.querySelectorAll('a[href*="digitaloceanspaces.com"]');
    s3Links.forEach((link) => this.attachHandlerToLink(link));
  }

  /**
   * Remove all click handlers
   */
  private removeAllHandlers(): void {
    const s3Links = document.querySelectorAll('a[href*="digitaloceanspaces.com"]');
    s3Links.forEach((link) => {
      link.removeEventListener('click', this.boundS3Click);
      this.handledLinks.delete(link);
    });
  }

  /**
   * Set up mutation observer for dynamically added content
   */
  private setupMutationObserver(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.observer = new MutationObserver(() => {
      // Chat streaming causes many DOM mutations; coalesce scans to at most once per frame.
      if (this.mutationRafId != null) {
        return;
      }
      this.mutationRafId = requestAnimationFrame(() => {
        this.mutationRafId = null;
        this.attachHandlers();
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Create and export a singleton instance
const simpleS3Handler = new SimpleS3Handler();

export default simpleS3Handler;
export { SimpleS3Handler };
